/** ABS API client — same cookie session as Data Room (`/api` → auth :8100). */

async function req(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/abs${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || j.message || JSON.stringify(j);
    } catch {
      /* ignore */
    }
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function requireDealId(dealId) {
  const id = typeof dealId === 'string' ? dealId.trim() : '';
  if (!id) {
    throw new Error('dealId is required for ABS GET helpers');
  }
  return id;
}

function withDealId(path, dealId) {
  const id = requireDealId(dealId);
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}dealId=${encodeURIComponent(id)}`;
}

export const absApi = {
  /** Phase-2 ABS link (approved forecasts → securitization). */
  linkOptions: (dealId) => req(`/link/${encodeURIComponent(requireDealId(dealId))}/options`),
  linkRun: (body) => req('/link/run', { method: 'POST', body }),
  linkFinancials: (body) => req('/link/financials', { method: 'POST', body }),
  linkCapStruct: (body = {}) => req('/link/capstruct', { method: 'POST', body }),
  health: () => req('/health'),
  deals: () => req('/deals'),
  run: (body = {}) => req('/run', { method: 'POST', body }),
  /** SPEC-221d: BASE + downside screen orchestration. */
  screen: (body = {}) => req('/screen', { method: 'POST', body }),
  scenarios: (dealId) => req(withDealId('/scenarios', dealId)),
  deskPresets: (dealId) => req(withDealId('/scenarios', dealId)),
  sensitivity: (dealId) => req(withDealId('/sensitivity', dealId)),
  /** Internal export — requires screenId (or base+downside hash pair). Never bare runHash. */
  exportOutput: (dealId, { screenId, baseRunHash, downsideRunHash } = {}) => {
    let path = withDealId('/export/abs-output.json', dealId);
    if (screenId) path += `&screenId=${encodeURIComponent(screenId)}`;
    if (baseRunHash) path += `&baseRunHash=${encodeURIComponent(baseRunHash)}`;
    if (downsideRunHash) path += `&downsideRunHash=${encodeURIComponent(downsideRunHash)}`;
    return req(path);
  },
  freeze: (body) => req('/freeze', { method: 'POST', body }),
  createScreen: (body) => req('/screens', { method: 'POST', body }),
  gaps: (dealId, runHash) =>
    req(`${withDealId('/gaps', dealId)}&runHash=${encodeURIComponent(runHash)}`),
  /** SPEC-221e paired eligibility — screenId or hash pair. */
  eligibility: (dealId, { screenId, baseRunHash, downsideRunHash } = {}) => {
    let path = withDealId('/export/eligibility', dealId);
    if (screenId) path += `&screenId=${encodeURIComponent(screenId)}`;
    if (baseRunHash) path += `&baseRunHash=${encodeURIComponent(baseRunHash)}`;
    if (downsideRunHash) path += `&downsideRunHash=${encodeURIComponent(downsideRunHash)}`;
    return req(path);
  },
  counterpartyOutput: (dealId, capitalCaseId, view = 'agency') =>
    req(
      `${withDealId('/export/counterparty/abs-output.json', dealId)}&capitalCaseId=${encodeURIComponent(capitalCaseId)}&view=${encodeURIComponent(view)}`,
    ),
  bidCurve: (dealId) => req(withDealId('/bid-curve', dealId)),
  getRun: (hash) => req(`/runs/${hash}`),
};
